import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CdkDragDrop, DragDropModule, transferArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { AdminService } from '../../../services/admin.service';
import { ServerResponse, ServerResponseObject } from '../../../interfaces/ServerResponse';
import { UserDialogData } from '../add-user-company-dialog/add-user-company-dialog.component';
import { forkJoin } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Role, PermissionListItem, AllRoles, Functionality, PostRole } from '../../../interfaces/admin/Role';

@Component({
  selector: 'app-add-permissions-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatCheckboxModule,
    DragDropModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './add-permissions-user-dialog.component.html',
  styleUrl: './add-permissions-user-dialog.component.scss'
})
export class AddPermissionsUserDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AddPermissionsUserDialogComponent>, { optional: true });
  data = inject<UserDialogData>(MAT_DIALOG_DATA);

  constructor(private adminService: AdminService) {
    this.getAllRoles();
  }

  displayedColumns = ['select', 'name'];

  originalRoles: Role[] = [];

  availableRoles: PermissionListItem[] = [];

  assignedRoles: PermissionListItem[] = [];

  selectedAvailable = new Set<string>();
  selectedAssigned = new Set<string>();

  isLoading: boolean = false;

  toggleSelect(list: 'available' | 'assigned', role: Role) {
    const set = list === 'available' ? this.selectedAvailable : this.selectedAssigned;
    if (set.has(role.id)) set.delete(role.id); else set.add(role.id);
  }

  drop(event: CdkDragDrop<PermissionListItem[]> ) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
    this.availableRoles = [...this.availableRoles];
    this.assignedRoles = [...this.assignedRoles];
  }

  getAllRoles(){
    this.isLoading = true;
    const requests = [
        this.adminService.ListUserRoles(this.data.uuid),
        this.adminService.getRoles("", 1, 100),
    ];
    forkJoin(requests).subscribe({
      next: ([res1, res2])=>{
        const list1 = (res1 as ServerResponseObject<Role>).result.roles;
        const list2 = (res2 as ServerResponseObject<AllRoles>).result.roles;
        if(!list1 || !list2) {
          this.availableRoles = this.convertIntoPermissionList(list2);
          this.assignedRoles = [];
          this.isLoading = false;
          return;
        }
        let availableList = list2.filter((role: Role)=> !list1!.some((func: Functionality)=>func.id === role.id));
        this.availableRoles = this.convertIntoPermissionList(availableList);
        this.assignedRoles = this.convertIntoPermissionList(list1);
        this.isLoading = false;
      },
      error: ()=>{
        this.isLoading = false;
      }
    });
  }

  convertIntoPermissionList(list: Role[] | Functionality[]){
    return list.map((role: Role | Functionality)=> { return { name: role.name, id: role.id, uuid: role.uuid } as PermissionListItem });
  }

  sendRoleList(){
    let roles_UUID = this.assignedRoles.map((item: PermissionListItem)=> item.uuid);
    let json = {
      user_UUID: this.data.uuid,
      role_UUID: roles_UUID
    } as PostRole;
    this.isLoading = true;
    this.adminService.setUserRolesByList(json).subscribe({
      next: (res: ServerResponseObject<any>)=>{
        this.isLoading = false;
        this.dialogRef?.close(true);
      },
      error: ()=>{
        this.isLoading = false;
      }
    });
  }

  onCancel() {
    this.dialogRef?.close();
  }
}
